import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPayouts() {
  const { token } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("pending");
  const [stats, setStats] = useState({ pending: 0, completed: 0 });

  useEffect(() => {
    fetchPayouts();
  }, [page, status]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/payouts?page=${page}&limit=20&status=${status}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setPayouts(data.payouts || []);
      setTotal(data.total || 0);
      
      // Stats hesapla
      const pendingRes = await fetch(
        `${API_URL}/api/admin/payouts?status=pending&limit=1000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const pendingData = await pendingRes.json();
      const pendingTotal = (pendingData.payouts || []).reduce((sum, p) => sum + p.amount, 0);
      
      const completedRes = await fetch(
        `${API_URL}/api/admin/payouts?status=completed&limit=1000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const completedData = await completedRes.json();
      const completedTotal = (completedData.payouts || []).reduce((sum, p) => sum + p.amount, 0);
      
      setStats({ pending: pendingTotal, completed: completedTotal });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleProcess = async (payoutId, newStatus, notes = "") => {
    try {
      await fetch(
        `${API_URL}/api/admin/payouts/${payoutId}/process?status=${newStatus}&admin_notes=${encodeURIComponent(notes)}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      fetchPayouts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/payouts/export?status=pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      
      // CSV oluştur
      let csv = "Satıcı,IBAN,Banka,Tutar,Hakediş ID\n";
      data.payouts.forEach(p => {
        csv += `"${p.vendor_name}","${p.iban}","${p.bank_name}",${p.amount},"${p.payout_id}"\n`;
      });
      
      // İndir
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hakedis_odeme_listesi_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBadge = (s) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800"
    };
    const labels = {
      pending: "Bekliyor",
      processing: "İşleniyor",
      completed: "Ödendi",
      failed: "Başarısız"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[s]}`}>
        {labels[s]}
      </span>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Hakediş Yönetimi</h1>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          IBAN Listesi İndir
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 p-6 rounded-lg shadow">
          <p className="text-sm text-yellow-600">Bekleyen Ödemeler</p>
          <p className="text-3xl font-bold text-yellow-700">{stats.pending.toLocaleString()} ₺</p>
        </div>
        <div className="bg-green-50 p-6 rounded-lg shadow">
          <p className="text-sm text-green-600">Tamamlanan Ödemeler</p>
          <p className="text-3xl font-bold text-green-700">{stats.completed.toLocaleString()} ₺</p>
        </div>
        <div className="bg-blue-50 p-6 rounded-lg shadow">
          <p className="text-sm text-blue-600">Bu Ay Ödenen</p>
          <p className="text-3xl font-bold text-blue-700">-</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {["pending", "processing", "completed", "failed"].map(s => (
          <button
            key={s}
            onClick={() => { setStatus(s); setPage(1); }}
            className={`px-4 py-2 rounded-lg ${
              status === s 
                ? "bg-orange-600 text-white" 
                : "bg-white border hover:bg-gray-50"
            }`}
          >
            {s === "pending" ? "Bekleyen" :
             s === "processing" ? "İşleniyor" :
             s === "completed" ? "Ödenen" : "Başarısız"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Satıcı</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IBAN</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Banka</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tutar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center">Yükleniyor...</td>
              </tr>
            ) : payouts.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  Bu durumda hakediş bulunmuyor
                </td>
              </tr>
            ) : (
              payouts.map((payout) => (
                <tr key={payout.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium">{payout.vendor_name}</td>
                  <td className="px-6 py-4 text-sm font-mono">{payout.iban}</td>
                  <td className="px-6 py-4 text-sm">{payout.bank_name}</td>
                  <td className="px-6 py-4 font-bold text-green-600">{payout.amount.toLocaleString()} ₺</td>
                  <td className="px-6 py-4">{getStatusBadge(payout.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(payout.created_at).toLocaleDateString("tr-TR")}
                  </td>
                  <td className="px-6 py-4">
                    {payout.status === "pending" && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleProcess(payout.id, "completed")}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          Ödendi
                        </button>
                        <button
                          onClick={() => handleProcess(payout.id, "failed", "Ödeme başarısız")}
                          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                        >
                          Başarısız
                        </button>
                      </div>
                    )}
                    {payout.status !== "pending" && (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Hakediş Ödeme Süreci</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Siparişler teslim edildikten sonra <strong>7-14 gün blokeli</strong> olarak tutulur</li>
          <li>• Bloke süresi dolduktan sonra satıcı bakiyesine eklenir</li>
          <li>• Satıcı çekim talebi oluşturabilir veya otomatik ödeme planlanabilir</li>
          <li>• <strong>IBAN Listesi İndir</strong> ile toplu banka ödemesi yapılabilir</li>
        </ul>
      </div>
    </div>
  );
}
